/**
 * FiberEdge - Custom React Flow edge for fiber connections
 */
import React from 'react';
import { EdgeProps, getBezierPath, EdgeLabelRenderer } from '@xyflow/react';

export interface FiberEdgeData {
  fiberNumber?: number;
  cableId?: number;
  loss: number;
  distance?: number;
  isPassThrough?: boolean;
  passThrough?: boolean;
  passThroughPole?: string;
  fromCableId?: number;
  splice?: boolean;
  splicePole?: string;
  microtubeSize?: string;
  microtubeColor?: string;
  microtubeType?: string;
  fiberColor?: string;
  path?: Array<{ type: 'passthrough' | 'splice' | 'cable', poleId?: number, poleName?: string, cableId?: number, distance?: number }>;
  pathDescription?: string;
  isReverseFiber?: boolean;
  // Visibility flags
  showCableInfo?: boolean;
  showCableDistance?: boolean;
  showPassthrough?: boolean;
  showSplice?: boolean;
  showFiberColor?: boolean;
}

// Fiber color mapping (1-12)
const FIBER_COLORS: Record<number, string> = {
  1: '#0000FF',  // Blue
  2: '#FF8C00',  // Orange
  3: '#00FF00',  // Green
  4: '#8B4513',  // Brown
  5: '#708090',  // Slate
  6: '#FFFFFF',  // White
  7: '#FF0000',  // Red
  8: '#000000',  // Black
  9: '#FFFF00',  // Yellow
  10: '#8B00FF', // Violet
  11: '#FF69B4', // Rose
  12: '#00FFFF', // Aqua
};

const FIBER_COLOR_NAMES: Record<number, string> = {
  1: 'Modrá',
  2: 'Oranžová',
  3: 'Zelená',
  4: 'Hnedá',
  5: 'Sivá',
  6: 'Biela',
  7: 'Červená',
  8: 'Čierna',
  9: 'Žltá',
  10: 'Fialová',
  11: 'Ružová',
  12: 'Tyrkysová',
};

export const FiberEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data: edgeData,
  selected,
}) => {
  const data = (edgeData as unknown as FiberEdgeData) || {};
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Color based on fiber number OR loss (for hierarchical view)
  let edgeColor = '#999';
  if (data?.fiberNumber) {
    edgeColor = FIBER_COLORS[data.fiberNumber] || '#999';
  } else if (data?.loss !== undefined) {
    // Color code by loss: green (low) → yellow (medium) → red (high)
    if (data.loss < 5) {
      edgeColor = '#4caf50'; // Green
    } else if (data.loss < 10) {
      edgeColor = '#ff9800'; // Orange
    } else {
      edgeColor = '#f44336'; // Red
    }
  }

  const strokeWidth = selected ? 4 : 3;

  return (
    <>
      {/* Main fiber path */}
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        stroke={edgeColor}
        strokeWidth={strokeWidth}
        fill="none"
        style={{
          filter: selected ? 'drop-shadow(0 0 4px rgba(0,0,0,0.3))' : 'none',
        }}
      />

      {/* Edge label - only show if showCableInfo is true */}
      {data?.showCableInfo !== false && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="fiber-edge-label"
          >
            <div
              style={{
                background: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                border: `2px solid ${edgeColor}`,
                fontSize: '11px',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap',
              }}
            >
            {data?.fiberNumber && (
              <div style={{ color: edgeColor, display: 'flex', alignItems: 'center', gap: '4px' }}>
                {data?.showFiberColor !== false && (
                  <span style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: FIBER_COLORS[data.fiberNumber] || '#999',
                    border: '1px solid #333',
                    flexShrink: 0
                  }}></span>
                )}
                <span>
                  Vlákno č.{data.fiberNumber}
                  {data?.showFiberColor !== false && ` (${FIBER_COLOR_NAMES[data.fiberNumber] || 'Neznáma'})`}
                </span>
              </div>
            )}
            {data?.cableId && (
              <div style={{ color: '#666', fontSize: '10px' }}>
                Cable #{data.cableId}
              </div>
            )}
            {/* Show reverse fiber indicator */}
            {data?.isReverseFiber && (
              <div style={{ color: '#ff5722', fontSize: '10px', fontWeight: 600, marginTop: '4px' }}>
                ⬅️ Reverzný smer
              </div>
            )}
            {/* Show path with all segments (cable, passthrough, splice) */}
            {data?.path && data.path.length > 0 && (
              <div style={{ marginTop: '4px', borderTop: '1px solid #eee', paddingTop: '4px' }}>
                {data.path.map((point, idx) => {
                  if (point.type === 'cable' && data?.showCableDistance !== false) {
                    const distance = parseFloat(String(point.distance || 0));
                    return (
                      <div key={idx} style={{
                        color: '#2196f3',
                        fontSize: '10px',
                        marginTop: idx > 0 ? '2px' : '0'
                      }}>
                        📏 Cable #{point.cableId}: {distance.toFixed(2)}m
                      </div>
                    );
                  } else if (point.type === 'passthrough' && data?.showPassthrough !== false) {
                    return (
                      <div key={idx} style={{
                        color: '#ff9800',
                        fontSize: '10px',
                        fontWeight: 600,
                        marginTop: idx > 0 ? '2px' : '0'
                      }}>
                        🔄 Pass-through at {point.poleName}
                      </div>
                    );
                  } else if (point.type === 'splice' && data?.showSplice !== false) {
                    return (
                      <div key={idx} style={{
                        color: '#9c27b0',
                        fontSize: '10px',
                        fontWeight: 600,
                        marginTop: idx > 0 ? '2px' : '0'
                      }}>
                        🔗 Splice at {point.poleName}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}
            {/* Fallback to old single passthrough/splice display if no path */}
            {!data?.path && (data?.isPassThrough || data?.passThrough) && (
              <div style={{ color: '#ff9800', fontSize: '10px', fontWeight: 600 }}>
                🔄 Pass-through
                {data?.passThroughPole && ` via ${data.passThroughPole}`}
              </div>
            )}
            {!data?.path && data?.passThrough && data?.fromCableId && (
              <div style={{ color: '#999', fontSize: '9px' }}>
                (Cable #{data.fromCableId} → #{data.cableId})
              </div>
            )}
            {!data?.path && data?.splice && (
              <div style={{ color: '#9c27b0', fontSize: '10px', fontWeight: 600 }}>
                🔗 Splice
                {data?.splicePole && ` at ${data.splicePole}`}
              </div>
            )}
            {!data?.path && data?.splice && data?.fromCableId && (
              <div style={{ color: '#999', fontSize: '9px' }}>
                (Cable #{data.fromCableId} → #{data.cableId})
              </div>
            )}
            {data?.microtubeSize && (
              <div style={{ color: '#2196f3', fontSize: '10px' }}>
                📏 {data.microtubeSize} mm
              </div>
            )}
            {data?.microtubeColor && (
              <div style={{ color: '#4caf50', fontSize: '10px' }}>
                🎨 {data.microtubeColor}
              </div>
            )}
            {data?.microtubeType && (
              <div style={{ color: '#9c27b0', fontSize: '10px' }}>
                🔧 {data.microtubeType}
              </div>
            )}
            {/* Calculate total distance and loss from path */}
            {(() => {
              let totalDistance = 0;
              let totalLoss = 0;

              if (data?.path && data.path.length > 0) {
                data.path.forEach(point => {
                  if (point.type === 'cable' && point.distance) {
                    const distance = parseFloat(String(point.distance));
                    totalDistance += distance;
                    // Fiber loss: 0.35 dB/km = 0.00035 dB/m
                    totalLoss += distance * 0.00035;
                  } else if (point.type === 'passthrough') {
                    totalLoss += 0.01; // Passthrough loss
                  } else if (point.type === 'splice') {
                    totalLoss += 0.03; // Splice loss
                  }
                });
              }

              return (
                <>
                  <div style={{ color: '#333', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #eee' }}>
                    <strong>Loss:</strong> {totalLoss.toFixed(2)} dB
                  </div>
                  {data?.showCableDistance !== false && totalDistance > 0 && (
                    <div style={{ color: '#333', fontSize: '11px', fontWeight: 600 }}>
                      <strong>Celková dĺžka:</strong> {totalDistance.toFixed(2)}m
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </EdgeLabelRenderer>
      )}
    </>
  );
};

